import { List, Map } from "immutable"
import * as React from "react"
import { errorText, getActivityClusters, getDatesForPeriod, loadingText, Region } from "../../custom_api/api"
import { emptyOpt, makeOpt } from "../../custom_utils/option"
import { ActivityCluster, ActivityClusterProps } from "../components/activity_cluster"
import { Jumbo } from "../components/jumbo"
import { RegionAndDateFilter, PeriodSlug } from "../components/region_and_date_filter"
import { SearchBarWrapper } from "../components/search_bar"
import { QuickLinks, QuickLink } from "../components/quick_links";
import { Link } from "../components/library/link";
import { setFilterParam } from "../components/filters"
import { ApiResult } from "../../../Shared/api/apiTypes"
import { BasePageProps } from "../components/page_renderer"

export interface ActivityClustersPageProps extends BasePageProps {
  readonly kind: "activityClusters"
  readonly pageTitle: string
  readonly headerTitle: string
  readonly description: string
  readonly imageUrl: string
  readonly imageUrlLarge: string
  readonly phImageUrlSmall?: string
  readonly phImageUrl: string
  readonly phImageUrlLarge: string
  readonly imageAlt: string
  readonly regions: Map<string, Region>
  readonly selectedPeriod: PeriodSlug
  readonly selectedRegion: string
  readonly selectedCluster: string
  readonly selectPeriod: (e: React.ChangeEvent<HTMLSelectElement>) => void
  readonly selectRegion: (e: React.ChangeEvent<HTMLSelectElement>) => void
  readonly toggleArchive: (cb?: () => void) => void
  readonly showArchive: boolean
}

interface ActivityClustersPageState {
  readonly clusters: ApiResult<List<ActivityClusterProps>>
}

const activityClustersPageStateZero: ActivityClustersPageState = {
  clusters: "loading",
}

const tomorrow = new Date(`${(new Date().getFullYear())}-${(new Date().getMonth() + 1)}-${(new Date()).getDate() + 1}`)

export class ActivityClustersPage extends React.Component<ActivityClustersPageProps, ActivityClustersPageState> {
  constructor(props: ActivityClustersPageProps) {
    super(props)

    this.state = activityClustersPageStateZero

    this.updateResults = this.updateResults.bind(this)
  }

  componentDidMount() {
    this.updateResults(this.props, () => this.scrollToCluster(this.props.selectedCluster))

    document.title = this.props.pageTitle
  }

  scrollToCluster(slug: string) {
    setTimeout(() => {
      if (this.state.clusters != "error" && this.state.clusters != "loading") {
        let cluster = this.state.clusters.filter(c => c.slug === slug)
  
        if (!cluster.isEmpty()) {
          let element = document.querySelector(`[data-anchor="${slug}"]`)
          if (!element) return
          element.scrollIntoView({ behavior: "smooth" })
        }
      }
    }, 500)
  }

  componentWillReceiveProps(nextProps: ActivityClustersPageProps) {
    const p = this.props
    const np = nextProps

    const scrollToCluster = () => p.selectedCluster != np.selectedCluster ? this.scrollToCluster(np.selectedCluster) : undefined

    if (p.selectedPeriod == np.selectedPeriod && p.selectedRegion == np.selectedRegion) {
      scrollToCluster()
      return
    }

    this.updateResults(np, () => scrollToCluster())
  }

  toggleArchive() {
    this.props.toggleArchive(() => this.updateResults(this.props))
  }

  updateResults(props: ActivityClustersPageProps, cb?: () => void) {
    this.setState(
      (s, _) => ({ ...s, clusters: "loading" }),
      () => {
        getActivityClusters({
          cluster: emptyOpt(),
          edition: emptyOpt(),
          region: makeOpt(q => 
            props.selectedRegion 
              ? q.Where(r => r.Slug.Equals(props.selectedRegion)) 
              : q  
          ),
          activity: makeOpt(q => q.Where(a =>
            a.HideInOverview.Equals(false)
          )),
          location: emptyOpt(),
          event: makeOpt(
            q => this.props.showArchive
              ? q.Where(e => e.Date.IsBefore(tomorrow))
              : q.Where(e => {
                let period = getDatesForPeriod(props.selectedPeriod)
                var startDate = period.start.toDate()
                var utcStartDate = new Date(startDate.getTime() + startDate.getTimezoneOffset() * 60000)
                return e.Date.IsAfter(utcStartDate).And(e.Date.IsBefore(period.end.toDate()))
              })
          )
        }).then(x => {
          if (x != "error" && x != "loading") {
            this.setState(
              (s, _) => ({ ...s, clusters: x }),
              () => {
                if (cb) cb()
              }
            )
          }
        })
      }
    )
  }

  render() {
    const p = this.props
    const s = this.state

    return (
      <div>
        <Jumbo
          title={p.headerTitle}
          contents={p.description}
          imageUrl={p.phImageUrl}
          imageUrlLarge={p.phImageUrlLarge}
          imageAlt={p.imageAlt}
          size="small"
          kind="activity"
        />
        {this.props.showArchive ? null : (
          <SearchBarWrapper>
            <RegionAndDateFilter
              enabled={s.clusters != "loading" && s.clusters != "error"}
              selectedPeriod={p.selectedPeriod}
              selectedRegion={p.selectedRegion}
              selectPeriod={p.selectPeriod}
              selectRegion={p.selectRegion}
              regions={p.regions}
            />
          </SearchBarWrapper>
        )}
        <div aria-live="polite">
          {s.clusters == "loading" ? (
            <div className="container--mg align--center text--styled">
              <p className="weight--bold container--s">{loadingText}</p>
            </div>
          ) : s.clusters == "error" ? (
            <div className="container--mg align--center text--styled">
              <p className="weight--bold container--s">{errorText}</p>
            </div>
          ) : (
            !s.clusters.isEmpty() && (
              <>
                <div className="container--mg align--center">
                  <QuickLinks
                    title="Direct naar..."
                    links={s.clusters.map<QuickLink>((cluster) => ({title: cluster.title, url: cluster.slug})).toList()}
                    handleScroll={(slug: string) => { setFilterParam('categorie', slug); this.scrollToCluster(slug)}}
                  />
                </div>



                {s.clusters.map((x, k) => <ActivityCluster {...x} key={`activity-cluster-${k}`} />).toArray()}
              </>
            )
          )}
        </div>
        <div className="container--mg align--center">
          <a className="button button--primary button--arrow" href="#" onClick={() => this.toggleArchive()}>
            <span className="button__label">{this.props.showArchive ? "Terug naar het actuele overzicht" : "Bekijk het archief"}</span>
          </a>
          <Link className="button button--secondary" url="/terugblikken">
            <span className="button__label">Bekijk alle terugblikken</span>
          </Link>
        </div>
      </div>
    )
  }
}

{/* TH: Added to styleguide! */}
